import React, { useEffect, useState } from 'react'
import { Sheet, TextArea, XStack, YStack } from 'tamagui'
import GroupsIcon from '@mui/icons-material/Groups'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import ThumbUpIcon from '@mui/icons-material/ThumbUp'
import PlaceIcon from '@mui/icons-material/Place'
import { CobuAlert, CobuButton, CobuStack, CobuText } from '@cobu/ui'
import type { CommentModerationResult, Place, PlaceShareType } from '@cobu/types'
import { useShareToFeedMutation } from '@cobu/api-client'
import { showErrorMessage, showSuccessMessage } from '../../utils/messageToast'
import { Strings } from '../../constants/Strings'
import { getModerationAlertConfig } from '../../constants/moderation'
import { URLS } from '../../constants'

type ShareTypeKey = 'wantToGo' | 'question' | 'recommend'

const SHARE_TYPES: Array<{ type: PlaceShareType; key: ShareTypeKey; icon: React.ComponentType<any> }> = [
  { type: 'want_to_go', key: 'wantToGo', icon: GroupsIcon },
  { type: 'question', key: 'question', icon: HelpOutlineIcon },
  { type: 'recommend', key: 'recommend', icon: ThumbUpIcon },
]

const KEY_BY_TYPE: Record<PlaceShareType, ShareTypeKey> = {
  want_to_go: 'wantToGo',
  question: 'question',
  recommend: 'recommend',
}

interface ShareToFeedSheetProps {
  place: Place
  open: boolean
  onOpenChange: (open: boolean) => void
  initialShareType?: PlaceShareType
}

// Localized default copy for a type — read live so it follows the active language.
const placeholderFor = (type: PlaceShareType) =>
  Strings.get().places.share.placeholders[KEY_BY_TYPE[type]]

export function ShareToFeedSheet({ place, open, onOpenChange, initialShareType = 'want_to_go' }: ShareToFeedSheetProps) {
  const strings = Strings.get().places
  const [shareType, setShareType] = useState<PlaceShareType>(initialShareType)
  // The per-type placeholder copy is the actual default value of the field, not
  // just a placeholder attribute — so if the resident never edits it, that text
  // is what gets posted. They can still edit it before posting.
  const [comment, setComment] = useState(() => placeholderFor(initialShareType))
  const [moderation, setModeration] = useState<CommentModerationResult | null>(null)
  const shareToFeedMutation = useShareToFeedMutation()

  // Each time the sheet opens (or opens to a new type), seed the type and the
  // default text for it.
  useEffect(() => {
    if (open) {
      setShareType(initialShareType)
      setComment(placeholderFor(initialShareType))
      setModeration(null)
    }
  }, [open, initialShareType])

  // Switching type swaps in that type's default copy.
  const selectType = (type: PlaceShareType) => {
    setShareType(type)
    setComment(placeholderFor(type))
  }

  // Sharing to the Feed creates a real group comment, so it runs through the
  // Feed's moderation stack. A blocked share resolves (200) with a verdict
  // rather than throwing — the sheet stays open on that path so the resident can
  // edit, and must not claim it posted.
  const postShare = async (userDecision?: 'SEND_ANYWAY') => {
    try {
      const verdict = await shareToFeedMutation.mutateAsync({
        place: place.uuid,
        shareType,
        // Fall back to the type's default copy if they cleared the field.
        comment: comment.trim() || placeholderFor(shareType),
        userDecision,
      })
      if (verdict) {
        setModeration(verdict)
        return
      }
      showSuccessMessage(strings.share.postedToast)
      onOpenChange(false)
    } catch {
      showErrorMessage(strings.genericError)
    }
  }

  const handlePost = () => postShare()

  const moderationConfig = moderation ? getModerationAlertConfig(moderation.moderationType) : null
  const moderationStrings = Strings.get().discussion.moderationAlerts

  const handleModerationDecision = (decision: string) => {
    setModeration(null)
    switch (decision) {
      case moderationStrings.sentiment.options.sendAnyway:
        void postShare('SEND_ANYWAY')
        break
      case moderationStrings.profanity.options.viewCodeOfConduct:
      case moderationStrings.sentiment.options.viewCodeOfConduct:
        if (URLS.CODE_OF_CONDUCT) {
          window.open(URLS.CODE_OF_CONDUCT, '_blank', 'noopener,noreferrer')
        }
        break
      // "Edit Post" just dismisses — the sheet is still open behind the alert.
    }
  }

  return (
    <Sheet modal open={open} onOpenChange={onOpenChange} snapPoints={[65, 40]} position={0} dismissOnSnapToBottom>
      <Sheet.Overlay style={{ backgroundColor: 'rgba(0,0,0,0.45)' }} />
      <Sheet.Handle />
      <Sheet.Frame f={1} bg="$background" p="$4" gap="$3">
        <CobuText variant="heading" fontSize="$4">{strings.share.title}</CobuText>

        <XStack gap="$2">
          {SHARE_TYPES.map(({ type, key, icon: Icon }) => {
            const isSel = type === shareType
            return (
              <CobuStack
                key={type}
                f={1}
                ai="center"
                gap="$1"
                p="$3"
                br="$3"
                bg={isSel ? '$backgroundSecondary' : 'transparent'}
                borderWidth={1}
                borderColor={isSel ? '$color' : '$borderColor'}
                onPress={() => selectType(type)}
              >
                <Icon style={{ fontSize: 20 }} />
                <CobuText variant="caption" fontWeight="500" ta="center">{strings.share.types[key]}</CobuText>
              </CobuStack>
            )
          })}
        </XStack>

        <XStack ai="center" gap="$2" p="$3" br="$3" bg="$backgroundSecondary">
          <PlaceIcon style={{ fontSize: 16 }} />
          <YStack>
            <CobuText variant="body" fontWeight="600" fontSize="$2">{place.name}</CobuText>
            <CobuText variant="caption" color="$colorSecondary">{place.street}</CobuText>
          </YStack>
        </XStack>

        <TextArea
          value={comment}
          onChangeText={setComment}
          placeholder={placeholderFor(shareType)}
          numberOfLines={3}
          br="$3"
          p="$3"
        />

        <CobuButton
          dsVariant="primary"
          onPress={handlePost}
          loading={shareToFeedMutation.isPending}
          title={strings.share.post}
        />

        {moderationConfig && (
          <CobuAlert
            open
            onOpenChange={(isOpen) => !isOpen && setModeration(null)}
            title={moderationConfig.title}
            description={moderationConfig.message}
            actions={moderationConfig.options.map((option) => ({
              text: option.text,
              onPress: () => handleModerationDecision(option.text),
              style: option.style,
            }))}
          />
        )}
      </Sheet.Frame>
    </Sheet>
  )
}
